import { AppData } from '@lightningjs/sdk';
import Lightning from '@lightningjs/sdk/src/Lightning';
import { DirectionsType } from 'support/keyEventUtils';
const ENTER_STRING = 'Enter';

export type LocalCoordsFromClick = {
  x: number;
  y: number;
};

export async function dispatchEnter() {
  const keyCodeMap = AppData?.device.getKeyMapping();

  if (!keyCodeMap) return;

  const enterKeyEvent = await AppData!.device.getEnterKeyEvent(
    keyCodeMap,
    ENTER_STRING,
  );
  document.dispatchEvent(enterKeyEvent);
}

export function appendCollision(element: Lightning.Element) {
  if (!element.collision) element.collision = true;
  if (
    element.ref !== 'App' &&
    element.parent &&
    (!element.parent.collision ||
      (typeof element.parent.collision === 'number' &&
        element.parent.collision === 2))
  ) {
    appendCollision(element.parent);
  }
}

export interface HoverableComponentSignalMap
  extends Lightning.Component.SignalMap {
  $onHover(target: unknown): void;
  $onUnHover(target: unknown): void;
  $onCursorClick(target: unknown, localCoords: LocalCoordsFromClick): void;
}

export interface HoverableComponentTypeConfig
  extends Lightning.Component.TypeConfigLoose {
  SignalMapType: HoverableComponentSignalMap;
}

export class HoverableComponent<
  TemplateSpec extends Lightning.Component.TemplateSpec = Lightning.Component.TemplateSpec,
  TypeConfig extends Lightning.Component.TypeConfig &
    HoverableComponentTypeConfig = Lightning.Component.TypeConfig &
    HoverableComponentTypeConfig,
> extends Lightning.Component<TemplateSpec, TypeConfig> {
  protected hovered = false;
  protected isClickable = true;

  private swipeActionRef:
    | ((direction: DirectionsType, strength: number) => void)
    | null = null;

  protected onSwipeAction:
    | null
    | ((direction: DirectionsType, strength: number) => void) = null;

  override _setup() {
    super._setup();
    appendCollision(this);
  }

  override _inactive() {
    if (this.swipeActionRef) {
      this.application.removeListener('swipeAction', this.swipeActionRef);
      this.swipeActionRef = null;
    }
  }

  override _active() {
    if (this.onSwipeAction) {
      this.swipeActionRef = this.onSwipeAction.bind(this);
      this.application.on('swipeAction', this.swipeActionRef);
    }
  }

  get isHoverable() {
    return !AppData?.device.hoverDisabled();
  }

  get isHovered() {
    return this.hovered;
  }

  _handleClick(target: any, localCoords: LocalCoordsFromClick) {
    if (!this.isClickable) return;

    (this as HoverableComponent).signal('$onCursorClick', target, localCoords);
    if (!this.hovered || !this.isHoverable) return;

    if (!AppData?.device.canMouseClick()) return;

    // fire off promise without waiting
    dispatchEnter();
  }

  _handleHover(target: any) {
    if (!this.isHoverable) {
      return true;
    }

    this.hovered = true;
    (this as HoverableComponent).signal('$onHover', target);

    return false;
  }

  _handleUnhover(target: any) {
    if (!this.isHoverable) {
      return true;
    }

    this.hovered = false;
    (this as HoverableComponent).signal('$onUnhover', target);

    return false;
  }
}
